import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ReportRunDetailDrawer } from "@/components/admin/ReportRunDetailDrawer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Activity,
  Clock,
} from "lucide-react";
import { format, subDays } from "date-fns";
import { cn } from "@/lib/utils";
import {
  useReportRuns,
  useReportRunDetail,
  type ReportRun,
} from "@/hooks/use-admin-report-runs";

const STATUSES = [
  { value: "_all", label: "All Statuses" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "processing", label: "Processing" },
];

const MODALITIES = [
  { value: "_all", label: "All Modalities" },
  { value: "CT", label: "CT" },
  { value: "MR", label: "MR" },
  { value: "XR", label: "XR" },
  { value: "US", label: "US" },
  { value: "NM", label: "NM" },
];

const DATE_RANGES = [
  { value: "24h", label: "Last 24 hours", days: 1 },
  { value: "7d", label: "Last 7 days", days: 7 },
  { value: "30d", label: "Last 30 days", days: 30 },
  { value: "all", label: "All time", days: null },
];

function getStatusBadge(status: string) {
  switch (status) {
    case "completed":
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    case "processing":
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
          <Activity className="h-3 w-3 mr-1 animate-pulse" />
          Processing
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export default function ReportRuns() {
  // State
  const [statusFilter, setStatusFilter] = useState("_all");
  const [modalityFilter, setModalityFilter] = useState("_all");
  const [dateRange, setDateRange] = useState("7d");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Hooks
  const { runs, total, page, pageSize, loading, fetch } = useReportRuns();
  const {
    detail,
    loading: detailLoading,
    fetch: fetchDetail,
    clear: clearDetail,
  } = useReportRunDetail();

  // Build filters
  const getFilters = () => {
    const filters: {
      startDate?: Date;
      endDate?: Date;
      status?: string;
      modality?: string;
      userId?: string;
    } = {};

    const range = DATE_RANGES.find((r) => r.value === dateRange);
    if (range?.days) {
      filters.startDate = subDays(new Date(), range.days);
      filters.endDate = new Date();
    }

    if (statusFilter && statusFilter !== "_all") filters.status = statusFilter;
    if (modalityFilter && modalityFilter !== "_all") filters.modality = modalityFilter;
    if (searchTerm) filters.userId = searchTerm; // Simple search by user ID

    return filters;
  };

  // Initial fetch
  useEffect(() => {
    fetch(getFilters());
  }, []);

  // Refetch on filter change
  useEffect(() => {
    fetch(getFilters(), 1);
  }, [statusFilter, modalityFilter, dateRange]);

  // Handle row click
  const handleRowClick = (run: ReportRun) => {
    setSelectedRunId(run.run_id);
    setDrawerOpen(true);
    fetchDetail(run.run_id);
  };

  // Handle drawer close
  const handleDrawerClose = (open: boolean) => {
    setDrawerOpen(open);
    if (!open) {
      setSelectedRunId(null);
      clearDetail();
    }
  };

  // Handle search
  const handleSearch = () => {
    fetch(getFilters(), 1);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetch(getFilters(), page);
  };

  // Handle pagination
  const handlePrevPage = () => {
    if (page > 1) {
      fetch(getFilters(), page - 1);
    }
  };

  const handleNextPage = () => {
    if (page * pageSize < total) {
      fetch(getFilters(), page + 1);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <AdminLayout
      title="Report Runs"
      description="Monitor report generation runs and debug errors"
    >
      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-48"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button variant="outline" size="icon" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Date Range */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Modality Filter */}
            <Select value={modalityFilter} onValueChange={setModalityFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Modality" />
              </SelectTrigger>
              <SelectContent>
                {MODALITIES.map((mod) => (
                  <SelectItem key={mod.value} value={mod.value}>
                    {mod.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="ml-auto"
            >
              <RefreshCw
                className={cn("h-4 w-4 mr-2", loading && "animate-spin")}
              />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run ID</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Exam</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  </TableRow>
                ))
              ) : runs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No report runs found
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run) => (
                  <TableRow
                    key={run.run_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(run)}
                  >
                    <TableCell className="font-mono text-xs">
                      {run.run_id.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="text-sm">
                      {run.user_email || run.user_id.slice(0, 8) + "..."}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{run.exam_family_id}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(run.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {run.duration_ms ? (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {run.duration_ms.toLocaleString()}ms
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(run.created_at), "MMM d, HH:mm")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {(page - 1) * pageSize + 1} -{" "}
                {Math.min(page * pageSize, total)} of {total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <ReportRunDetailDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        detail={detail}
        loading={detailLoading}
      />
    </AdminLayout>
  );
}
